import { useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { getLabel, FOOD_SCOPE } from "../../enums/FoodDictionaryEnums";
import BrandButton from "./BrandButton";
import { t } from "../dashboard/coach/coachTokens";
import HealthFlagChips from "./HealthFlagChips";
import DietAllergenChips from "./DietAllergenChips";
import FoodGroupField from "./FoodGroupField";
import { RTL } from "../../cache/CacheRtl";
import { updateFoodDictionary } from "../../services/system/FoodDictionaryService";
import { updateFood as updateCompanyFood } from "../../services/FoodDictionaryService";
import { useMessageContext } from "../../contexts/MessageContext";
import { useAuth } from "../../contexts/AuthContext";

const Row = ({ label, value, mono }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
    <Typography sx={{ color: "#64748B", fontSize: "14px" }}>{label}</Typography>
    <Typography sx={{ color: "#1E293B", fontSize: "14px", fontWeight: 500, fontFamily: mono ? "monospace" : "inherit", textAlign: "left" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

const FoodDictionaryProfileSection = ({ food, canEdit = false, onUpdate }) => {
  const { notificationHandler } = useMessageContext();
  const { isSuperAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", brand: "", barcode: "" });

  const handleOpen = () => {
    setForm({
      name: food.name || "",
      brand: food.brand || "",
      barcode: food.barcode || "",
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      notificationHandler.error("יש למלא שם מזון");
      return;
    }

    setLoading(true);
    try {
      // A system admin edits through /system, which reaches any food. Everyone else goes through the
      // company route, which refuses anything their company does not own — so a system food is not
      // editable here even if this dialog were somehow opened on one.
      const save = isSuperAdmin() ? updateFoodDictionary : updateCompanyFood;
      await save(food.id, {
        name: form.name.trim(),
        brand: form.brand.trim() || null,
        barcode: form.barcode.trim() || null,
      });
      notificationHandler.success("פרטי המזון עודכנו");
      setOpen(false);
      onUpdate?.();
    } catch (error) {
      notificationHandler.error(error.response?.data?.message || error.response?.data || "שגיאה בעדכון");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Paper sx={{ p: 3, borderRadius: "12px", boxShadow: "0 1px 3px rgba(16,24,40,.1)", direction: "rtl" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#1E293B", mb: 1 }}>{food.name}</Typography>
            {food.brand && <Typography sx={{ fontSize: "16px", color: "#64748B", mb: 1.5 }}>{food.brand}</Typography>}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip label={getLabel(FOOD_SCOPE, food.scope)} size="small" sx={{ fontSize: "12px" }} />
              <Chip
                label={food.certified ? "מאושר" : "לא מאושר"}
                size="small"
                sx={{
                  fontSize: "12px",
                  backgroundColor: food.certified ? "#05966920" : "#DC262620",
                  color: food.certified ? "#059669" : "#DC2626",
                }}
              />
            </Box>
            <Box sx={{ mt: 1.25 }}>
              <HealthFlagChips food={food} />
            </Box>
            <Box sx={{ mt: 1 }}>
              <DietAllergenChips food={food} />
            </Box>
          </Box>
          {canEdit && (
            <IconButton onClick={handleOpen} sx={{ color: "#64748B" }}>
              <Edit />
            </IconButton>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1E293B", mb: 0.5 }}>מידע בסיסי</Typography>
            <Row label="ברקוד" value={food.barcode} mono />
            <Row label="מנה ברירת מחדל" value={food.defaultServingName} />
            {/* Sits in the basic-info column rather than beside the macros on purpose: the group is
                what the food IS, not how much of anything it contains, and "פחמימות" as a group must
                not be read as the "פחמימות" gram count one column over. Editing is system-admin only
                because the endpoint is a /system route — a company admin sees the answer, read-only. */}
            <FoodGroupField food={food} canEdit={isSuperAdmin()} onUpdate={onUpdate} />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1E293B", mb: 0.5 }}>מאקרו — מנה ברירת מחדל</Typography>
            <Row label="קלוריות" value={food.defaultServingNutrition?.calories ?? "—"} />
            <Row label="חלבון" value={food.defaultServingNutrition?.protein != null ? `${food.defaultServingNutrition.protein} גרם` : "—"} />
            <Row label="פחמימות" value={food.defaultServingNutrition?.carbs != null ? `${food.defaultServingNutrition.carbs} גרם` : "—"} />
            <Row label="שומן" value={food.defaultServingNutrition?.fats != null ? `${food.defaultServingNutrition.fats} גרם` : "—"} />
          </Box>
        </Box>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: t.r, direction: "rtl" } }}>
        <DialogTitle sx={{ fontSize: "18px", fontWeight: 700, color: t.text }}>עריכת פרטי מזון</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            <RTL>
              <TextField
                fullWidth
                label="שם המזון *"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </RTL>
            <RTL>
              <TextField
                fullWidth
                label="מותג"
                value={form.brand}
                onChange={(e) => setForm((p) => ({ ...p, brand: e.target.value }))}
              />
            </RTL>
            <RTL>
              <TextField
                fullWidth
                label="ברקוד"
                value={form.barcode}
                onChange={(e) => setForm((p) => ({ ...p, barcode: e.target.value }))}
              />
            </RTL>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <BrandButton variant="secondary" onClick={() => setOpen(false)} disabled={loading}>ביטול</BrandButton>
          <BrandButton variant="primary" onClick={handleSave} disabled={loading}>{loading ? "שומר..." : "שמור"}</BrandButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FoodDictionaryProfileSection;
